/**
 * @module services/mediaErrorMessage
 *
 * Turns raw video-playback error info into short, user-friendly text.
 * Extracted out of MediaPlayer.vue so the standalone quick-play dialog can
 * show the same messages without duplicating the mapping.
 */

/**
 * Map an HTML5 <video> element's MediaError code to a generic user-facing
 * message. This is the browser-level error (network/decode/aborted/etc.),
 * not the server-side download error — see humanizeError below for that.
 *
 * @param {number|undefined} code - video.error?.code (a MediaError.MEDIA_ERR_* constant)
 * @returns {string}
 */
export const mediaErrorToMessage = (code) => {
  switch (code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return "Video loading was aborted";
    case MediaError.MEDIA_ERR_NETWORK:
      return "Network error while loading the video";
    case MediaError.MEDIA_ERR_DECODE:
      return "Video decoding error";
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return "Video not available";
    default:
      return "An error occurred while loading the video";
  }
};

/**
 * Map known raw yt-dlp/server error strings to short, friendly in-player text.
 * Falls back to the original message when nothing matches.
 *
 * Mirrors the server-side mapping in services/video.js, but also covers errors
 * that were stored as raw text BEFORE that mapping existed (already in the DB),
 * so the player still renders them nicely without a re-download.
 *
 * @param {string} msg - The raw error/progress_message string
 * @returns {string} A friendly message, or the original if unmatched
 */
export const humanizeError = (msg) => {
  if (!msg) return msg;
  // YouTube anti-bot challenge ("Sign in to confirm you're not a bot…").
  if (/not a bot/i.test(msg)) {
    return "YouTube blocked the download (bot check) — try again later";
  }
  return msg;
};
